import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Create axios instance with auth interceptor
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Interfaces
export interface Client {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
  dateOfBirth?: string;
  createdAt?: string;
  updatedAt?: string;
  idVerificationStatus?: 'pending' | 'verified' | 'rejected' | null;
  idVerificationDate?: string;
  idRequestSent?: boolean;
  idRequestSentDate?: string;
  claimCount?: number;
  claims?: any[];
  // Conflict detection fields
  conflictFound?: boolean;
  conflictMarkedAt?: string;
  lastUpdated?: string;
}

export interface ClientsResponse {
  clients: Client[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface ProcessingStats {
  total_claims: number;
  processed_claims: number;
  failed_claims: number;
  pending_claims: number;
  unique_clients: number;
  normalized_claims: number;
  processing_percentage: number;
}

export interface ProcessingResult {
  total_processed: number;
  successful: number;
  failed: number;
  already_processed: number;
  dry_run: boolean;
  errors: Array<{
    claimId?: string;
    error: string;
  }>;
}

// Client API functions
export const clientsApi = {
  // Get paginated list of clients
  async getClients(page: number = 1, pageSize: number = 20, search?: string): Promise<ClientsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) {
      params.append('search', search);
    }
    const response = await axiosInstance.get<ClientsResponse>(`/ui/clients?${params}`);
    return response.data;
  },

  // Get single client by ID
  async getClient(clientId: string): Promise<Client> {
    const response = await axiosInstance.get<Client>(`/ui/clients/${clientId}`);
    return response.data;
  },

  // Create new client
  async createClient(clientData: Partial<Client>): Promise<Client> {
    const response = await axiosInstance.post<Client>('/ui/clients', clientData);
    return response.data;
  },

  // Update client
  async updateClient(clientId: string, updates: Partial<Client>): Promise<Client> {
    const response = await axiosInstance.put<Client>(`/ui/clients/${clientId}`, updates);
    return response.data;
  },

  // Get all claims for a client
  async getClientClaims(clientId: string): Promise<{ clientId: string; claims: any[]; count: number }> {
    const response = await axiosInstance.get<{ clientId: string; claims: any[]; count: number }>(`/ui/clients/${clientId}/claims`);
    return response.data;
  },

  // Search client by email
  async searchByEmail(email: string): Promise<Client | null> {
    try {
      const response = await axiosInstance.get<Client>(`/ui/clients/search/by-email?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
};

// Processing API functions
export const processingApi = {
  // Process claims batch
  async processClaims(batchSize: number = 100, dryRun: boolean = false): Promise<ProcessingResult> {
    try {
      const response = await axiosInstance.post<ProcessingResult>('/ui/admin/processor/process-claims', {
        batchSize,
        dryRun,
      });
      return response.data;
    } catch (error: any) {
      console.error('Process claims error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to process claims');
    }
  },

  // Get processing statistics
  async getProcessingStats(): Promise<ProcessingStats> {
    const response = await axiosInstance.get<ProcessingStats>('/ui/admin/processor/processing-stats');
    return response.data;
  },

  // Process single claim
  async processSingleClaim(claimId: string): Promise<{ success: boolean; clientId?: string; normalizedClaimId?: string; error?: string }> {
    const response = await axiosInstance.post<{ success: boolean; clientId?: string; normalizedClaimId?: string; error?: string }>(`/ui/admin/processor/process-single-claim/${claimId}`);
    return response.data;
  },

  // Process all claims for a specific email
  async processClaimsByEmail(email: string): Promise<{
    success: boolean;
    email: string;
    total_claims: number;
    processed: number;
    successful: number;
    failed: number;
    errors: Array<{ claimId?: string; error: string }>;
    client_id: string | null;
    client_name: string | null;
    message?: string;
  }> {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        email: string;
        total_claims: number;
        processed: number;
        successful: number;
        failed: number;
        errors: Array<{ claimId?: string; error: string }>;
        client_id: string | null;
        client_name: string | null;
        message?: string;
      }>('/ui/admin/processor/process-by-email', { email });
      return response.data;
    } catch (error: any) {
      console.error('Process by email error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to process claims by email');
    }
  },
};